import React from "react"
import { Link } from "gatsby"
import { Container, Spacer } from "@bw/bits"
import * as Icons from "@bw/icons"
import styled from "styled-components"
import useGlobalPages from "hooks/useGlobalPages"
import useGlobalCompany from "hooks/useGlobalCompany"
import useSocialNetworks from "hooks/useSocialNetworks"
import Poi from "models/poi"
import Logo from "../header/logo"
import { useTranslation } from "react-i18next"
import useFooterMenu from "../../../hooks/useFooterMenu"
import * as css from "./footer.module.css"

const Title = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 19px;
  color: var(--text-color);
  margin-bottom: 0.5em;
`

const Footer = () => {
  const { t } = useTranslation()
  const pages = useGlobalPages()
  const company = useGlobalCompany()
  const networks = useSocialNetworks()
  const poi = new Poi({
    country: company.country,
    city: company.city,
    email: company.email,
    phone: company.phone,
    street: company.street,
    zipCode: company.zipcode,
  })
  const menu = useFooterMenu()

  const lines = poi.getAddressLines()

  return (
    <footer className={`${css.footer} footer`}>
      <Container>
        <div className={`${css.grid} footer__top`}>
          <div>
            <Title>{company.sitename}</Title>
            {lines.length > 0 &&
              lines.map((line, i) => <div key={i}>{line}</div>)}
          </div>
          <div>
            {company.email && (
              <div>
                <a href={`mailto:${company.email}`}>{company.email}</a>
              </div>
            )}
            {company.phone && <div>{company.phone}</div>}
          </div>
          <div />
          <div className={css.alignRight}>
            {networks && (
              <Spacer direction="row" gap="small">
                {networks.map((network, i) => {
                  const iconName =
                    network.networkType.label.charAt(0).toUpperCase() +
                    network.networkType.label.slice(1).toLowerCase()
                  const Icon =
                    typeof Icons[iconName] !== "undefined"
                      ? Icons[iconName]
                      : null
                  return (
                    <a
                      className={css.socialNetwork}
                      key={i}
                      href={network.networkUrl}
                      rel="noreferrer nofollow"
                      target="_blank"
                      title={network.networkType.label}
                    >
                      <div>
                        <Icon
                          fillColor="var(--primary)"
                          width="15px"
                          height="15px"
                        />
                      </div>
                    </a>
                  )
                })}
              </Spacer>
            )}
          </div>
        </div>
        {menu && menu.length > 0 && (
          <nav className={`${css.navgrid} ${css.line} footer__navigation`}>
            {menu.map(({ column }, i) => {
              return (
                <div key={i}>
                  {column.map((item, k) => {
                    if (item.__typename === "TitleFooterColumnItem") {
                      return (
                        <div key={i + "_" + k} className={css.navTitle}>
                          {item.title}
                        </div>
                      )
                    }

                    if (item.__typename === "TextFooterColumnItem") {
                      return (
                        <div
                          key={i + "_" + k}
                          className={css.navText}
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                      )
                    }

                    if (item.__typename === "LinkFooterColumnItem") {
                      let link
                      if (item.linkType.value === "entry" && !item.isExternal) {
                        link = <Link to={item.entry.url}>{item.linkText}</Link>
                      } else if (
                        item.linkType.value === "entry" &&
                        item.isExternal
                      ) {
                        link = (
                          <a
                            href={item.entry.url}
                            target="_blank"
                            rel="noopener"
                          >
                            {item.linkText}
                          </a>
                        )
                      } else if (item.linkType.value === "url") {
                        link = (
                          <a
                            href={item.url}
                            target={item.isExternal ? "_blank" : null}
                            rel={item.isExternal ? "noopener" : null}
                          >
                            {item.linkText}
                          </a>
                        )
                      }
                      return (
                        <div key={i + "_" + k} className={css.navLink}>
                          {link}
                        </div>
                      )
                    }
                  })}
                </div>
              )
            })}
          </nav>
        )}
        <div className={`${css.grid} ${css.line} footer__bottom`}>
          <div className="footer__copyright">
            Copyright &copy;{company.sitename}, {new Date().getFullYear()}
          </div>
          <div className={`${css.legal} footer__legal`}>
            <span style={{ marginRight: "1.5rem" }}>Politique de confidentialité</span>
            <Link to="/conditions-d-utilisation">Conditions d'utilisation</Link>
          </div>
          {!process.env.GATSBY_DONT_SHOW_CREDITS && (
            <div
              className={`${css.bureauweb} ${css.alignRight} footer__credits`}
            >
              Site web par le&nbsp;
              <a
                href="https://www.bureauweb.ch/site-internet/creation-de-sites-internet/"
                title="Conception du site Internet par Le Bureau Web"
              >
                <Logo width="75" height="15" fillColor="var(--bw-logo-color)" />
              </a>
            </div>
          )}
        </div>
      </Container>
    </footer>
  )
}

export { Footer }
